import { useState, type FormEvent } from "react";

import { Badge } from "../../shared/ui/badge";
import { Button } from "../../shared/ui/button";
import { Field, FieldError } from "../../shared/ui/field";
import { Input } from "../../shared/ui/input";
import { Label } from "../../shared/ui/label";
import {
  OnboardingContent,
  OnboardingFrame,
  OnboardingHeading,
} from "./OnboardingFrame";
import type { AuthAction, DevSignInAction } from "./onboardingTypes";
import { errorCode, errorDetail } from "./onboardingTypes";

interface DevGoogleSignInProps {
  onDevSignIn: DevSignInAction;
}

export function DevGoogleSignIn({ onDevSignIn }: DevGoogleSignInProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !email.includes("@")) return;
    setBusy(true);
    setError(null);
    try {
      await onDevSignIn({ email: email.trim(), name: name.trim() });
    } catch (signInError) {
      setError(`Dev sign-in failed (${errorDetail(signInError)})`);
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mt-1 grid gap-2 border-t border-dashed border-warning/60 pt-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[0.68rem] font-bold tracking-[0.12em] text-warning uppercase">
          Emulator dev sign-in
        </span>
        <Badge variant="warning">Local only</Badge>
      </div>
      <Field>
        <Label htmlFor="dev-google-email">Email</Label>
        <Input
          id="dev-google-email"
          type="email"
          required
          placeholder="you@example.com"
          aria-label="Dev sign-in email"
          value={email}
          disabled={busy}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>
      <Field>
        <Label htmlFor="dev-google-name">Display name</Label>
        <Input
          id="dev-google-name"
          type="text"
          placeholder="Optional"
          aria-label="Dev sign-in display name"
          value={name}
          disabled={busy}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Button
        type="submit"
        disabled={busy || !email.includes("@")}
        pending={busy}
        pendingLabel="SIGNING IN…"
        variant="outline"
        className="border-dashed border-warning text-warning"
      >
        FAKE GOOGLE SIGN-IN
      </Button>
      {error ? <FieldError className="text-center">{error}</FieldError> : null}
    </form>
  );
}

function authErrorMessage(error: unknown): string | null {
  const code = errorCode(error);
  if (
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request"
  ) {
    return null;
  }
  if (code === "auth/popup-blocked") {
    return "Your browser blocked the sign-in window. Allow popups and try again.";
  }
  return "Couldn't sign you in. Check your connection and try again.";
}

interface LandingProps {
  onImport?: (() => void | Promise<void>) | undefined;
  onSignInGoogle?: AuthAction | undefined;
  onDevSignIn?: DevSignInAction | undefined;
  signedInEmail?: string | null | undefined;
}

export function Landing({
  onImport,
  onSignInGoogle,
  onDevSignIn,
  signedInEmail,
}: LandingProps) {
  const [busy, setBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async () => {
    if (!onSignInGoogle) return;
    setBusy(true);
    setError(null);
    try {
      await onSignInGoogle();
    } catch (signInError) {
      setError(authErrorMessage(signInError));
    } finally {
      setBusy(false);
    }
  };
  const importData = async () => {
    if (!onImport || importBusy) return;
    setImportBusy(true);
    try {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      await onImport();
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <OnboardingFrame>
      <OnboardingHeading
        title="DUGOUT STATS"
        description={
          signedInEmail
            ? `No team is linked to ${signedInEmail}. Open a team invite once, or use a different Google account.`
            : "This scorebook is invite-only. Ask a teammate for an invite link to join your team."
        }
      />
      <OnboardingContent>
        {onSignInGoogle ? (
          <>
            <Button
              type="button"
              onClick={signIn}
              pending={busy}
              pendingLabel="SIGNING IN…"
              disabled={importBusy}
              className="w-full"
            >
              {signedInEmail
                ? "USE A DIFFERENT GOOGLE ACCOUNT"
                : "SIGN IN WITH GOOGLE"}
            </Button>
            {!signedInEmail ? (
              <p className="m-0 text-center text-xs text-foreground-muted">
                Already linked your account? Sign in to open your team on this
                device.
              </p>
            ) : null}
            {error ? (
              <FieldError className="text-center">{error}</FieldError>
            ) : null}
          </>
        ) : null}
        {onDevSignIn ? <DevGoogleSignIn onDevSignIn={onDevSignIn} /> : null}
        {onImport ? (
          <Button
            type="button"
            onClick={importData}
            pending={importBusy}
            pendingLabel="STARTING IMPORT…"
            disabled={busy}
            size="sm"
            variant="ghost"
            className="w-full border border-dashed border-border"
          >
            IMPORT EXISTING LOCAL DATA
          </Button>
        ) : null}
      </OnboardingContent>
    </OnboardingFrame>
  );
}

interface JoinAuthChoiceProps {
  onSignInGoogle?: AuthAction | undefined;
  onDevSignIn?: DevSignInAction | undefined;
  onContinueAnonymous: AuthAction;
}

export function JoinAuthChoice({
  onSignInGoogle,
  onDevSignIn,
  onContinueAnonymous,
}: JoinAuthChoiceProps) {
  const [busy, setBusy] = useState<"google" | "anonymous" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (mode: "google" | "anonymous", action: AuthAction) => {
    setBusy(mode);
    setError(null);
    try {
      await action();
    } catch (signInError) {
      setError(authErrorMessage(signInError));
      setBusy(null);
    }
  };

  return (
    <OnboardingFrame>
      <OnboardingHeading
        title="JOIN YOUR TEAM"
        description="Use Google for the same player and team on every device, or continue anonymously on this device."
      />
      <OnboardingContent>
        {onSignInGoogle ? (
          <Button
            type="button"
            onClick={() => run("google", onSignInGoogle)}
            disabled={busy !== null}
            pending={busy === "google"}
            pendingLabel="SIGNING IN…"
            className="w-full"
          >
            CONTINUE WITH GOOGLE
          </Button>
        ) : null}
        <Button
          type="button"
          onClick={() => run("anonymous", onContinueAnonymous)}
          disabled={busy !== null}
          pending={busy === "anonymous"}
          pendingLabel="CONTINUING…"
          variant="outline"
          className="w-full"
        >
          CONTINUE WITHOUT GOOGLE
        </Button>
        {onDevSignIn ? <DevGoogleSignIn onDevSignIn={onDevSignIn} /> : null}
        {error ? (
          <FieldError className="text-center">{error}</FieldError>
        ) : null}
      </OnboardingContent>
    </OnboardingFrame>
  );
}
